import React, {useState, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import DataTable from "../../data-table/data-table";
import Topbar from "../../top-bar/top-bar";
import {instanceMob} from "../../../utils/utils";
import useLocationStore from "../config/use-location-store";
import {getBackendError} from "../../../utils/error-helpers";
import InlineModal from "../../info-modal/infoModal";
import Tooltip from "../../tooltip/tooltip";
import FormField from "../../forms/form-field";
import debounce from "lodash.debounce";
import "./styles.css";
import ProductRegistrationModal from "../modals/product-registration-modal";

const ProductsInventoryTable = () => {
    const navigate = useNavigate();
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [infoModal, setInfoModal] = useState(null);
    const [lastFailedRow, setLastFailedRow] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useRef(null);
    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showProductModal, setShowProductModal] = useState(false);

    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null,
        currentPage: 1,
    });

    // --- Modal handlers ---
    const handleOpenModal = (row) => {
        setSelectedProduct(row);
        setShowProductModal(true);
    };

    const handleCloseModal = () => {
        setShowProductModal(false);
        setSelectedProduct(null);
    };

    const handleProductSaved = () => {
        handleCloseModal();
        fetchProducts();
    };

    // --- Fetch products ---
    const fetchProducts = async () => {
        if (!selectedLocation?.id) return;
        setIsLoading(true);
        try {
            const res = await instanceMob.get(
                `/admin/accounting/products/?location_id=${selectedLocation.id}&page=${pagination.currentPage}${searchTerm}`
            );
            const data = res.data.results || res.data;

            const formattedData = data.map((item) => {
                const currentStock = item?.quantity ?? 0;
                const minStock = item?.min_stock ?? 0;
                let rowStatus = "ok";

                if (item.inactive_status) rowStatus = "blocked";
                else if (currentStock <= 0) rowStatus = "danger";
                else if (currentStock <= minStock) rowStatus = "warn";

                return {
                    id: item?.product_store_id,
                    productId: item.product_id,
                    name: item.name,
                    categories: item.categories || [],
                    brand: item.brand || "N/A",
                    unitPrice: item.unit_price,
                    salePrice: item.sale_price_per_unit,
                    minStock,
                    quantity: currentStock,
                    isBlocked: item.inactive_status,
                    stockAlert: rowStatus,
                    __rowStatus: rowStatus,

                    total_purchase_price: item.total_purchase_price || 0,
                    sale_price_per_unit: item.sale_price_per_unit || 0,
                    provider: item.provider || "",
                    value_paid: item.value_paid ?? 0,
                    value_pending: item.value_pending ?? 0,
                    payment_method: item.payment_method ?? "",
                    other_payment_method: item.other_payment_method ?? "",
                };
            });

            setTableData(formattedData);
            setPagination((prev) => ({
                ...prev,
                count: res.data.count ?? formattedData.length,
                next: res.data.next,
                previous: res.data.previous,
            }));
        } catch (err) {
            console.error("Error cargando productos:", err);
            const errorMessage = getBackendError(err);
            setInfoModal({
                iconType: "error",
                title: "Error al cargar productos",
                description: errorMessage,
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const columns = [
        {
            header: "ID Producto",
            accessor: "id",
            className: "text-center justify-content-start align-self-center",
            renderCell: (row) => (
                <div className="d-flex align-items-center justify-content-start gap-1">
                    <Tooltip
                        title={row.isBlocked ? "Desbloquear producto" : "Bloquear producto"}
                        position="top"
                    >
                        <button
                            className="btn btn-sm btn-link p-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleBlock(row);
                            }}
                        >
                            {row.isBlocked ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                     fill="none">
                                    <path d="M14 11L10 15L14 19" stroke="#3177BE" stroke-width="2"/>
                                    <path
                                        d="M4.20577 12.75C3.21517 11.8921 2.8184 10.8948 3.077 9.91263C3.3356 8.9305 4.23511 8.01848 5.63604 7.31802C7.03696 6.61756 8.86101 6.1678 10.8253 6.0385C12.7895 5.9092 14.7842 6.10758 16.5 6.60289C18.2158 7.09819 19.5567 7.86273 20.3149 8.77792C21.0731 9.69312 21.2061 10.7078 20.6933 11.6647C20.1806 12.6215 19.0507 13.467 17.4789 14.0701C15.9071 14.6731 13.9812 15 12 15"
                                        stroke="#3177BE" stroke-width="2" stroke-linecap="round"/>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                     fill="none">
                                    <circle cx="12" cy="12" r="9" stroke="#394F65" stroke-width="2"/>
                                    <path d="M18 18L6 6" stroke="#394F65" stroke-width="2"/>
                                </svg>
                            )}
                        </button>
                    </Tooltip>
                    <span className={`datatable-cell text-truncate ${row.isBlocked ? "blocked-cell" : ""}`}>
                        {row.id}
                    </span>
                </div>
            )
        },
        {
            header: "Producto",
            accessor: "name",
            className: "text-center justify-content-center align-self-center",
            renderCell: (row) => (
                <span
                    className={`datatable-cell text-truncate ${row.isBlocked ? "blocked-cell" : ""}`}>{row.name}</span>
            )
        },
        {
            header: "Categoría",
            renderCell: (row) => (
                <div className={`product-list gap-1 ${row.isBlocked ? "blocked-cell" : ""}`}>
                    {row.categories.map((cat, i) => (
                        <div key={i}
                             className={`datatable-cell text-truncate p-0 ${row.isBlocked ? "blocked-cell" : ""}`}>{cat}</div>
                    ))}
                </div>
            ),
            className: "text-center justify-content-center align-self-center"
        },
        {
            header: "Marca",
            accessor: "brand",
            className: "text-center justify-content-center align-self-center",
            renderCell: (row) => (
                <span
                    className={`datatable-cell text-truncate ${row.isBlocked ? "blocked-cell" : ""}`}>{row.brand}</span>
            )
        },
        {
            header: "Precio por unidad",
            accessor: "unitPrice",
            className: "text-center justify-content-center align-self-center",
            editable: true,
            type: "currency",
            renderCell: (row) => (
                <span
                    className={`datatable-cell text-truncate ${row.isBlocked ? "blocked-cell" : ""}`}>{row.unitPrice}</span>
            )
        },
        {
            header: "Precio de Venta",
            accessor: "salePrice",
            className: "text-center justify-content-center align-self-center",
            editable: true,
            type: "currency",
            renderCell: (row) => (
                <span
                    className={`datatable-cell text-truncate ${row.isBlocked ? "blocked-cell" : ""}`}>{row.salePrice}</span>
            )
        },
        {
            header: "Stock Mínimo",
            accessor: "minStock",
            className: "text-center align-self-center d-flex justify-content-center",
            editable: true,
            type: "quantity",
            renderCell: (row) => (
                <span
                    className={`datatable-cell text-truncate ${row.isBlocked ? "blocked-cell" : ""}`}>{row.minStock}</span>
            )
        },
        {
            header: "Stock Actual",
            accessor: "quantity",
            className: "text-center align-self-center d-flex justify-content-center",
            editable: true,
            type: "quantity",
            renderCell: (row) => (
                <span
                    className={`datatable-cell text-truncate ${row.isBlocked ? "blocked-cell" : ""}`}>{row.quantity}</span>
            )
        },
        {
            header: "Alerta Stock",
            accessor: "stockAlert",
            className: "text-center justify-content-center align-self-center",
            renderCell: (row) => {
                const statusMap = {
                    ok: <span className={`inventory-page-chip success ${row.isBlocked ? "blocked-cell" : ""}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                            <path
                                d="M8.5 2C11.8137 2 14.5 4.68629 14.5 8C14.5 11.3137 11.8137 14 8.5 14C5.18629 14 2.5 11.3137 2.5 8C2.5 4.68629 5.18629 2 8.5 2ZM7.7666 8.51758L6.54102 7.29297L5.12695 8.70703L7.12695 10.707L7.90039 11.4814L8.60156 10.6406L11.9355 6.64062L10.3984 5.35938L7.7666 8.51758Z"
                                fill="#6FB75D"/>
                        </svg>
                    </span>,
                    warn: <span className={`inventory-page-chip warning ${row.isBlocked ? "blocked-cell" : ""}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                            <path
                                d="M4.04651 6.45463C5.65563 3.60734 6.46018 2.18369 7.56423 1.81722C8.17157 1.61561 8.82583 1.61561 9.43317 1.81722C10.5372 2.18369 11.3418 3.60734 12.9509 6.45463C14.56 9.3019 15.3646 10.7256 15.1232 11.8856C14.9904 12.5238 14.6633 13.1026 14.1887 13.5392C13.326 14.3327 11.717 14.3327 8.4987 14.3327C5.28047 14.3327 3.67135 14.3327 2.80867 13.5392C2.33409 13.1026 2.00696 12.5238 1.87418 11.8856C1.63283 10.7256 2.43739 9.3019 4.04651 6.45463Z"
                                stroke="#D1A940" stroke-width="1.15254"/>
                            <path
                                d="M8.66276 11.3333V8.66667C8.66276 8.3524 8.66276 8.19527 8.56509 8.0976C8.46749 8 8.31036 8 7.99609 8"
                                stroke="#D1A940" stroke-width="1.15254" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M8.49609 6H8.50284" stroke="#D1A940" stroke-width="1.15254" stroke-linecap="round"
                                  stroke-linejoin="round"/>
                        </svg>
                    </span>,
                    danger: <span className={`inventory-page-chip danger ${row.isBlocked ? "blocked-cell" : ""}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                            <path
                                d="M4.04651 6.45463C5.65563 3.60734 6.46018 2.18369 7.56423 1.81722C8.17157 1.61561 8.82583 1.61561 9.43317 1.81722C10.5372 2.18369 11.3418 3.60734 12.9509 6.45463C14.56 9.3019 15.3646 10.7256 15.1232 11.8856C14.9904 12.5238 14.6633 13.1026 14.1887 13.5392C13.326 14.3327 11.717 14.3327 8.4987 14.3327C5.28047 14.3327 3.67135 14.3327 2.80867 13.5392C2.33409 13.1026 2.00696 12.5238 1.87418 11.8856C1.63283 10.7256 2.43739 9.3019 4.04651 6.45463Z"
                                stroke="#B75D5E" stroke-width="1.15254"/>
                            <path
                                d="M8.66276 11.3333V8.66667C8.66276 8.3524 8.66276 8.19527 8.56509 8.0976C8.46749 8 8.31036 8 7.99609 8"
                                stroke="#B75D5E" stroke-width="1.15254" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M8.49609 6H8.50284" stroke="#B75D5E" stroke-width="1.15254" stroke-linecap="round"
                                  stroke-linejoin="round"/>
                        </svg>
                    </span>,
                    blocked: <span className={`inventory-page-chip blocked ${row.isBlocked ? "blocked-cell" : ""}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                          <circle cx="12.5" cy="12" r="9" fill="#7A99BA" fill-opacity="0.25"/>
                          <path d="M8.5 12H16.5" stroke="#607FA1" stroke-width="1.2"/>
                        </svg>
                    </span>,
                };
                return statusMap[row.stockAlert] || <span className="chip neutral">-</span>;
            }
        },
    ];

    // --- Toggle block ---
    const handleToggleBlock = async (row) => {
        if (!selectedLocation?.id) return;
        const payload = { inactive_status: !row.isBlocked };

        try {
            await instanceMob.patch(
                `/admin/accounting/product/${row.productId}/?location_id=${selectedLocation.id}`,
                payload
            );

            setTableData((prev) =>
                prev.map((item) => (item.id === row.id ? { ...item, isBlocked: !row.isBlocked } : item))
            );

            setInfoModal({
                iconType: row.isBlocked ? "success" : "warning",
                title: row.isBlocked ? "Producto Desbloqueado" : "Producto Bloqueado",
                description: row.isBlocked
                    ? "El producto ha sido desbloqueado."
                    : "El producto ha sido bloqueado.",
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        } catch (err) {
            const errorMessage = getBackendError(err);
            setInfoModal({
                iconType: "error",
                title: "Error al actualizar",
                description: errorMessage,
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        }
    };

    // --- Save inline changes ---
    const handleSaveRow = async (updatedRow) => {
        if (!selectedLocation?.id) return;

        const payload = {
            product_id: updatedRow.productId,
            location_id: selectedLocation.id,
            unit_price: updatedRow.unitPrice,
            sale_price_per_unit: updatedRow.salePrice,
            min_stock: updatedRow.minStock,
            quantity: updatedRow.quantity,
        };

        try {
            await instanceMob.patch(
                `/admin/accounting/product/${updatedRow.productId}/?location_id=${selectedLocation.id}`,
                payload
            );

            setLastFailedRow(null);
            fetchProducts();

            setInfoModal({
                iconType: "success",
                title: "Cambios guardados correctamente",
                description: "El producto fue actualizado con éxito.",
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        } catch (err) {
            const errorMessage = getBackendError(err);
            setLastFailedRow(updatedRow);
            setInfoModal({
                iconType: "error",
                title: "Error al guardar cambios",
                description: errorMessage,
                primaryAction: {
                    label: "Reintentar",
                    onClick: async () => {
                        setInfoModal(null);
                        await handleSaveRow(updatedRow);
                    },
                },
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        }
    };

    const handleNext = () =>
        pagination.next &&
        setPagination((p) => ({ ...p, currentPage: p.currentPage + 1 }));

    const handlePrevious = () =>
        pagination.previous &&
        setPagination((p) => ({ ...p, currentPage: Math.max(1, p.currentPage - 1) }));


    useEffect(() => {
        if (!selectedLocation?.id) return;
        if (debouncedSearch.current) debouncedSearch.current.cancel();
        debouncedSearch.current = debounce(fetchProducts, 400);
        debouncedSearch.current();
        return () => debouncedSearch.current?.cancel();
    }, [selectedLocation?.id, pagination.currentPage, searchTerm]);


    const handleDownloadExcel = async () => {
        if (!selectedLocation?.id) return;
        setIsDownloading(true);
        try {
            const res = await instanceMob.get(`/admin/accounting/products/excel`, {
                params: { location_id: selectedLocation.id },
                responseType: "blob",
            });
            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `products_inventory_location_${selectedLocation.id}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <>
            <div className="w-100">
                <Topbar/>
                <div className="inventory-page w-100 p-3 px-3 gap-4 d-flex flex-column">
                    <div className="d-flex flex-row justify-content-between align-items-center position-relative">
                        <div className="d-flex flex-row gap-4 align-items-center">
                            <div className="d-flex flex-row gap-1 justify-content-center align-items-center">
                                <button
                                    type="button"
                                    className="btn close-button p-1"
                                    onClick={() => navigate(-1)}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="36"
                                        height="36"
                                        viewBox="0 0 36 36"
                                        fill="none"
                                    >
                                        <path
                                            d="M25.5811 34.4648H9.88379C4.97749 34.4648 1 30.4874 1 25.5811V9.88379C1 4.97749 4.97749 1 9.88379 1H25.5811C30.4874 1 34.4648 4.97749 34.4648 9.88379V25.5811L34.4531 26.0381C34.223 30.5807 30.5807 34.223 26.0381 34.4531L25.5811 34.4648Z"
                                            stroke="#9DACBD"
                                            strokeWidth="2"
                                        />
                                        <path
                                            d="M11.0018 17.6351C11.0018 17.0388 11.2292 16.4425 11.6842 15.9875L20.7139 6.95782C21.6238 6.04792 23.0991 6.04792 24.009 6.95782C24.9189 7.86772 24.9189 9.34307 24.009 10.253L16.6268 17.6351L24.009 25.0172C24.9189 25.9271 24.9189 27.4024 24.009 28.3124C23.0991 29.2223 21.6238 29.2223 20.7139 28.3124L11.6842 19.2827C11.2292 18.8277 11.0018 18.2313 11.0018 17.6351Z"
                                            fill="#9DACBD"
                                        />
                                    </svg>
                                </button>
                                <div className="close-button-label">Volver</div>
                            </div>

                            <FormField
                                name="search"
                                type="search"
                                placeholder="Buscar producto"
                                width="269px"
                                isLoading={isLoading}
                                showLabel={false}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoComplete="off"
                            />
                        </div>

                        <div className="inventory-page-title position-absolute top-50 start-50 translate-middle">
                            Inventario de Productos
                        </div>

                        <button
                            type="button"
                            className="btn download-button"
                            disabled={isDownloading}
                            onClick={handleDownloadExcel}
                        >
                            Descargar Datos
                        </button>
                    </div>

                    <DataTable
                        columns={columns}
                        data={isLoading ? [] : tableData}
                        isLoading={isLoading}
                        variant="compact"
                        onSaveRow={handleSaveRow}
                        enableRowEdit={true}
                        editMode="modal"
                        onOpenModal={handleOpenModal}
                        pagination={{
                            count: pagination.count,
                            next: pagination.next,
                            previous: pagination.previous,
                            currentPage: pagination.currentPage,
                            onNext: handleNext,
                            onPrevious: handlePrevious,
                        }}
                    />
                </div>
            </div>

            {showProductModal && (
                <ProductRegistrationModal
                    show={showProductModal}
                    onClose={(refresh) => {
                        setShowProductModal(false);
                        if (refresh) fetchProducts();
                    }}
                    productData={selectedProduct}
                />
            )}

            {infoModal && (
                <InlineModal
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default ProductsInventoryTable;
